import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  MoreHorizontal,
  Eye,
  Download,
  Trash2,
  FilePlus2,
  Replace,
  Paperclip,
  Repeat,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";
import { api } from "../../lib/api";

/**
 * Row-level kebab menu for a document.
 *
 * Common options (all types):
 *   a. View analysis          → /analysis/:id
 *   b. Download               → /api/documents/:id/file
 *   e. Move to trash          → DELETE /api/documents/:id (with confirm)
 *
 * Conditional options (document_type sensitive):
 *   lease | contract | financing
 *     c. Add amendment        → /upload?attach_to=...&relationship_kind=amendment&doc_type=...
 *     d. Replace version      → same wiring, different copy
 *
 *   insurance
 *     c. Add endorsement      → relationship_kind=endorsement
 *     d. Attach certificate   → relationship_kind=annex   (non-modifying)
 *
 *   permit
 *     c. Renew this permit    → relationship_kind=renewal
 *
 *   invoice (replaces c/d)
 *     f. Mark as paid         → PATCH /api/documents/:id/invoice/payment-status
 *
 *   financials | other → no version-management option (by design).
 */
export default function DocumentRowActionsMenu({ doc, onRefresh }) {
  const { t } = useTranslation("documents");
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!doc) return null;
  const docType = (doc.document_type || "").toLowerCase();
  const paymentStatus = (doc.enrichment?.payment_status || "").toLowerCase();

  const handleView = () => navigate(`/analysis/${doc.id}`);

  const handleDownload = () => {
    // The download href works for PDF docs; manually-entered docs have no file.
    if (!doc.storage_path && doc.is_manual_entry) {
      toast.info(t("kebab.no_file_to_download"));
      return;
    }
    const a = document.createElement("a");
    a.href = doc.report_url || `/api/documents/${doc.id}/file`;
    a.download = doc.filename || `document-${doc.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const goAttachUpload = (relationshipKind, targetType) => {
    const params = new URLSearchParams({
      attach_to: doc.id,
      relationship_kind: relationshipKind,
      doc_type: targetType || docType,
    });
    navigate(`/upload?${params.toString()}`);
  };

  const handleMarkPaid = async () => {
    setBusy(true);
    try {
      await api.patch(`/documents/${doc.id}/invoice/payment-status`, {
        payment_status: "paid",
      });
      toast.success(t("kebab.invoice_marked_paid"));
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`${t("kebab.error_generic")} — ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t("kebab.deleted"));
      setConfirmOpen(false);
      onRefresh?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (status === 403) {
        toast.error(t("kebab.delete_forbidden"));
      } else {
        toast.error(`${t("kebab.error_generic")} — ${detail}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={`action-more-${doc.id}`}
            title={t("actions.more")}
            aria-label={t("actions.more")}
            className="w-7 h-7 grid place-items-center rounded-md text-zinc-400 hover:text-qenva-navy hover:bg-zinc-100 transition-colors outline-none focus:ring-2 focus:ring-qenva-blue/30"
          >
            <MoreHorizontal size={14} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="w-60"
          data-testid={`kebab-menu-${doc.id}`}
        >
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
            {t("kebab.label_common")}
          </DropdownMenuLabel>

          {/* a. View analysis */}
          <DropdownMenuItem
            onSelect={handleView}
            data-testid={`kebab-view-${doc.id}`}
            className="cursor-pointer"
          >
            <Eye size={14} className="text-zinc-500" />
            <span>{t("kebab.view_analysis")}</span>
          </DropdownMenuItem>

          {/* b. Download */}
          <DropdownMenuItem
            onSelect={handleDownload}
            data-testid={`kebab-download-${doc.id}`}
            className="cursor-pointer"
            disabled={!!doc.is_manual_entry}
          >
            <Download size={14} className="text-zinc-500" />
            <span>{t("kebab.download")}</span>
          </DropdownMenuItem>

          {/* Type-specific block */}
          {(docType === "lease" || docType === "contract" || docType === "financing") && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
                {t("kebab.label_versioning")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => goAttachUpload("amendment", docType)}
                data-testid={`kebab-amend-${doc.id}`}
                className="cursor-pointer"
              >
                <FilePlus2 size={14} className="text-qenva-cat-financing" />
                <span>{t("kebab.add_amendment")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => goAttachUpload("amendment", docType)}
                data-testid={`kebab-replace-${doc.id}`}
                className="cursor-pointer"
              >
                <Replace size={14} className="text-qenva-cat-financing" />
                <span>{t("kebab.replace_version")}</span>
              </DropdownMenuItem>
            </>
          )}

          {docType === "insurance" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
                {t("kebab.label_versioning")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => goAttachUpload("endorsement", "insurance")}
                data-testid={`kebab-endorsement-${doc.id}`}
                className="cursor-pointer"
              >
                <FilePlus2 size={14} className="text-qenva-cat-insurance" />
                <span>{t("kebab.add_endorsement")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => goAttachUpload("annex", "insurance")}
                data-testid={`kebab-certificate-${doc.id}`}
                className="cursor-pointer"
              >
                <Paperclip size={14} className="text-qenva-cat-insurance" />
                <span>{t("kebab.attach_certificate")}</span>
              </DropdownMenuItem>
            </>
          )}

          {docType === "permit" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
                {t("kebab.label_versioning")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => goAttachUpload("renewal", "permit")}
                data-testid={`kebab-renew-${doc.id}`}
                className="cursor-pointer"
              >
                <Repeat size={14} className="text-qenva-cat-permit" />
                <span>{t("kebab.renew_permit")}</span>
              </DropdownMenuItem>
            </>
          )}

          {docType === "invoice" && paymentStatus !== "paid" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
                {t("kebab.label_invoice")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={handleMarkPaid}
                disabled={busy}
                data-testid={`kebab-mark-paid-${doc.id}`}
                className="cursor-pointer"
              >
                {busy
                  ? <Loader2 size={14} className="text-emerald-600 animate-spin" />
                  : <CheckCircle2 size={14} className="text-emerald-600" />}
                <span>{t("kebab.mark_as_paid")}</span>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setConfirmOpen(true);
            }}
            data-testid={`kebab-trash-${doc.id}`}
            className="cursor-pointer text-qenva-cat-contract focus:text-qenva-cat-contract"
          >
            <Trash2 size={14} />
            <span>{t("kebab.move_to_trash")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`kebab-trash-confirm-${doc.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("kebab.trash_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("kebab.trash_dialog_body", { name: doc.filename })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={busy}
              data-testid={`kebab-trash-cancel-${doc.id}`}
            >
              {t("kebab.trash_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              data-testid={`kebab-trash-confirm-btn-${doc.id}`}
              className="bg-qenva-cat-contract hover:bg-qenva-cat-contract/90 text-white"
            >
              {busy
                ? <><Loader2 size={13} className="mr-1 animate-spin" /> {t("kebab.trash_dialog_pending")}</>
                : t("kebab.trash_dialog_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
